import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';

const en = (j: any) => (typeof j === 'string' ? j : j?.en || '');

export default function NewsAdmin() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['news-admin-list'],
    queryFn: async () => {
      const { data } = await supabase
        .from('news_articles')
        .select('id, slug, title, category, tags, is_published, published_at, view_count, author_name, is_featured, is_pinned, scheduled_for')
        .order('published_at', { ascending: false });
      return data || [];
    },
  });

  const togglePublish = async (id: string, val: boolean) => {
    const { error } = await supabase.from('news_articles').update({ is_published: val }).eq('id', id);
    if (error) toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    else qc.invalidateQueries({ queryKey: ['news-admin-list'] });
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this article? This cannot be undone.')) return;
    const { error } = await supabase.from('news_articles').delete().eq('id', id);
    if (error) toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted' }); qc.invalidateQueries({ queryKey: ['news-admin-list'] }); }
  };

  const filtered = rows.filter((r: any) => {
    if (!search.trim()) return true;
    const t = en(r.title).toLowerCase();
    return t.includes(search.toLowerCase()) || (r.category || '').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="container max-w-7xl py-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold">News articles</h1>
          <p className="text-sm text-muted-foreground">Manage published and draft news content.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild><Link to="/dashboard/news/taxonomy">Categories & tags</Link></Button>
          <Button variant="outline" asChild><Link to="/dashboard/news/analytics">Analytics</Link></Button>
          <Button variant="outline" asChild><Link to="/dashboard/news/audit">Audit log</Link></Button>
          <Button asChild><Link to="/dashboard/news/new"><Plus className="h-4 w-4 mr-1" /> New article</Link></Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Input placeholder="Search title or category…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="p-3">Title</th>
              <th className="p-3">Category</th>
              <th className="p-3">Author</th>
              <th className="p-3">Date</th>
              <th className="p-3">Views</th>
              <th className="p-3">Published</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!isLoading && filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No articles.</td></tr>}
            {filtered.map((r: any) => (
              <tr key={r.id} className="border-t">
                <td className="p-3">
                  <div className="font-medium flex items-center gap-1.5 flex-wrap">
                    {en(r.title) || '(untitled)'}
                    {r.is_pinned && <Badge variant="secondary" className="text-[10px]">Pinned</Badge>}
                    {r.is_featured && <Badge variant="secondary" className="text-[10px]">Featured</Badge>}
                    {!r.is_published && r.scheduled_for && new Date(r.scheduled_for) > new Date() && (
                      <Badge variant="outline" className="text-[10px]">Scheduled · {new Date(r.scheduled_for).toLocaleString()}</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">/{r.slug}</div>
                  {r.tags?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {r.tags.slice(0, 3).map((t: string) => <Badge key={t} variant="outline" className="text-[10px]">#{t}</Badge>)}
                    </div>
                  )}
                </td>
                <td className="p-3">{r.category || '—'}</td>
                <td className="p-3">{r.author_name || '—'}</td>
                <td className="p-3 whitespace-nowrap">{r.published_at ? new Date(r.published_at).toLocaleDateString() : '—'}</td>
                <td className="p-3">{r.view_count || 0}</td>
                <td className="p-3"><Switch checked={r.is_published} onCheckedChange={(v) => togglePublish(r.id, v)} /></td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    {r.slug && <Button variant="ghost" size="sm" asChild><a href={`/news/${r.slug}`} target="_blank" rel="noopener noreferrer"><Eye className="h-4 w-4" /></a></Button>}
                    <Button variant="ghost" size="sm" asChild><Link to={`/dashboard/news/${r.id}/edit`}><Edit2 className="h-4 w-4" /></Link></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
